import { getMomentTz } from "@dexteel/mesf-core";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import {
	Alert,
	Box,
	Button,
	Divider,
	Paper,
	Snackbar,
	Typography,
	useMediaQuery,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { CardPaperSx, capsSx } from "@/pages/sawlines/components/primitives";
import { ReclassifyBundleModal } from "@/pages/sawlines/components/ReclassifyBundleModal";
import { useQualityCheckRefresh } from "@/pages/sawlines/hooks/useQualityCheckRefresh";
import { useScanBurst } from "@/pages/sawlines/hooks/useScanBurst";
import {
	deleteBundleQualityCheck,
	reclassifyBundle,
	upsertBundleQualityCheck,
} from "@/pages/sawlines/repositories/sawWriteRepository";
import type { SawShiftBundle } from "@/pages/sawlines/repositories/sawBundlesRepository";
import {
	isOutOfSpec,
	type MeasurementValues,
	POSITIONS,
	type Position,
	qualityStatus,
	toNumber,
	unusualMessage,
	valuesFor,
} from "@/pages/sawlines/utils/qualityCheckMeasurements";
import { rangeWithFraction32, toFraction32 } from "@/utils/fraction";
import { TabletConfirm } from "../../components/TabletConfirm";
import { useEmployeeNumber } from "../../employee/EmployeeNumber";
import { cutLabel, qualityCheckPayload, typeKey } from "../models/qualityCheck";
import { FaceIcon, faceDots, MeasurementFace } from "./MeasurementFace";
import { MeasurementKeypad } from "./MeasurementKeypad";
import { QualityStatusChip } from "./QualityStatusChip";

const moment = getMomentTz();

export const TabletQualityEditor = ({
	cut,
	onBusyChange,
	onNotice,
	onSaved,
}: {
	cut: SawShiftBundle;
	onBusyChange: (busy: boolean) => void;
	onNotice: (message: string) => void;
	// in tolerance → parent moves on to the next unchecked cut
	onSaved: () => void;
}) => {
	const refresh = useQualityCheckRefresh();
	const { requireEmployee } = useEmployeeNumber();
	const [draft, setDraft] = useState<{
		cut: SawShiftBundle;
		values: MeasurementValues;
	} | null>(null);
	const scanBurst = useScanBurst();
	const beforeBurst = useRef(draft);
	const [position, setPosition] = useState<Position>("TL");
	// first key after picking a point replaces its value
	const [fresh, setFresh] = useState(true);
	const landscape = useMediaQuery("(orientation: landscape)");
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [confirm, setConfirm] = useState<{
		title: string;
		message: string;
		confirmLabel: string;
		onConfirm: () => void;
	} | null>(null);
	// employee # of whoever opened Reclassify/Restore
	const [reclassifying, setReclassifying] = useState<string | null>(null);
	const editingCut = draft?.cut ?? cut;
	const values = draft?.values ?? valuesFor(cut);
	const count = POSITIONS.filter((p) => toNumber(values[p.key]) != null).length;
	const outIn = (v: MeasurementValues) =>
		POSITIONS.some((p) => isOutOfSpec(editingCut, v[p.key]));
	const anyOut = outIn(values);
	const unusual = unusualMessage(editingCut, values);
	// point being typed isn't judged until the operator leaves it; Save judges all
	const typing = fresh ? null : position;
	const judged = typing ? { ...values, [typing]: "" } : values;
	const shownOut = outIn(judged);
	const shownUnusual = unusualMessage(editingCut, judged) != null;
	const status = qualityStatus(cut);
	const checkId = cut.QualityCheckId;
	const hasBounds =
		editingCut.MinLength != null && editingCut.MaxLength != null;
	// tolerance comes from the product's saw spec; the product from the WO
	const noTolerance = editingCut.IsCutback
		? "Cutback"
		: editingCut.WorkOrderId == null &&
				editingCut.ReclassifiedToProductId == null
			? "No work order"
			: "No saw spec";
	const locked = saving || confirm != null || reclassifying != null;
	const canSave = draft != null && count > 0 && !locked;

	useEffect(() => {
		onBusyChange(locked);
		return () => onBusyChange(false);
	}, [locked, onBusyChange]);

	const run = async (
		write: () => Promise<unknown>,
		notice: string,
		failed: string,
	) => {
		if (saving) return false;
		setSaving(true);
		setError("");
		try {
			await write();
			await refresh();
			onNotice(notice);
			return true;
		} catch (failure) {
			setError(failure instanceof Error ? failure.message : failed);
			return false;
		} finally {
			setSaving(false);
		}
	};

	const performSave = async (employee: string) => {
		const saved = await run(
			() =>
				upsertBundleQualityCheck({
					...qualityCheckPayload(editingCut, values),
					employeeNumber: employee,
				}),
			`${cutLabel(editingCut)} saved${editingCut.QualityCheckId != null ? " · original check time kept" : ""}`,
			"Unable to save the check.",
		);
		if (!saved) return;
		setDraft(null);
		if (!anyOut) return onSaved();
		setConfirm({
			title: "Measurements out of tolerance",
			message: `Some measurements are outside the product tolerance${hasBounds ? ` (${rangeWithFraction32(editingCut.MinLength!, editingCut.MaxLength!)})` : ""}. Reclassify this bundle?`,
			confirmLabel: "Reclassify",
			onConfirm: () => requireEmployee(setReclassifying),
		});
	};

	const save = () => {
		if (!canSave) return;
		if (!unusual) return requireEmployee((e) => void performSave(e));
		setConfirm({
			title: "Unusual measurements",
			message: unusual,
			confirmLabel: "Save anyway",
			onConfirm: () => requireEmployee((e) => void performSave(e)),
		});
	};

	const pick = (next: Position) => {
		setPosition(next);
		setFresh(true);
	};

	const enterKey = (key: string) => {
		if (locked) return;
		if (key === "next")
			return pick(
				POSITIONS[
					(POSITIONS.findIndex((p) => p.key === position) + 1) %
						POSITIONS.length
				].key,
			);
		const next = typeKey(values[position], key, fresh);
		if (next == null) return;
		setFresh(false);
		setDraft({ cut: editingCut, values: { ...values, [position]: next } });
	};

	const keypad = (
		<MeasurementKeypad column={landscape} disabled={locked} onKey={enterKey} />
	);

	const footer = (
		<Box
			sx={{
				px: 1.5,
				py: 1,
				mt: "auto",
				borderTop: "1px solid",
				borderColor: "divider",
				flexShrink: 0,
				display: "flex",
				alignItems: "center",
				gap: 1,
				"& button": { minHeight: 48, px: 2 },
			}}
		>
			<Box
				sx={{
					flex: 1,
					minWidth: 0,
					display: "flex",
					alignItems: "center",
					gap: 1.25,
				}}
			>
				<FaceIcon scale={1.4} dots={faceDots(editingCut, values, typing)} />
				<Box sx={{ minWidth: 0 }}>
					<Typography sx={{ fontSize: 15 }}>
						<Box component="strong" sx={{ fontSize: 18 }}>
							{count}/5
						</Box>{" "}
						points
					</Typography>
					<Typography
						variant="caption"
						color={shownUnusual ? "warning.dark" : "text.secondary"}
						sx={{ display: "block" }}
					>
						{shownUnusual
							? "Check the unusual value"
							: cut.CheckTime
								? `Checked ${moment(cut.CheckTime).format("MMM D HH:mm")}${
										cut.EmployeeNumber
											? ` · #${cut.EmployeeNumber}`
											: cut.CreatedBy
												? ` · ${cut.CreatedBy}`
												: ""
									}`
								: count < 5
									? "A partial check can be saved"
									: ""}
					</Typography>
				</Box>
			</Box>
			{checkId != null && !draft && (
				<Button
					variant="outlined"
					color="error"
					disabled={locked}
					onClick={() =>
						setConfirm({
							title: "Delete quality check?",
							message: `The saved measurements of ${cutLabel(cut)} will be removed.`,
							confirmLabel: "Delete",
							onConfirm: () =>
								requireEmployee(
									(e) =>
										void run(
											() => deleteBundleQualityCheck(checkId, e),
											`Check of ${cutLabel(cut)} deleted`,
											"Unable to delete the check.",
										),
								),
						})
					}
				>
					Delete check
				</Button>
			)}
			{draft && (
				<Button
					variant="outlined"
					color="inherit"
					disabled={locked}
					onClick={() => setDraft(null)}
				>
					Clear
				</Button>
			)}
			<Button variant="contained" disabled={!canSave} onClick={save}>
				{saving ? "Saving…" : "Save"}
			</Button>
		</Box>
	);

	return (
		<Box
			role="group"
			aria-label={`Measurements for ${cutLabel(cut)}. Use number keys, Backspace, or the Next point button.`}
			onKeyDown={(event) => {
				if (
					event.target instanceof HTMLInputElement ||
					event.target instanceof HTMLTextAreaElement
				)
					return;
				// scanner burst: undo its first key, swallow the rest and its Enter
				if (scanBurst(event)) {
					event.preventDefault();
					if (!locked) setDraft(beforeBurst.current);
					return;
				}
				beforeBurst.current = draft;
				if (locked) return;
				if (
					(event.ctrlKey || event.metaKey) &&
					event.key.toLowerCase() === "s"
				) {
					event.preventDefault();
					save();
				} else if (
					!event.ctrlKey &&
					!event.metaKey &&
					!event.altKey &&
					(/^[0-9.]$/.test(event.key) || event.key === "Backspace")
				) {
					event.preventDefault();
					enterKey(event.key);
				}
			}}
			sx={{
				display: "flex",
				flex: 1,
				gap: 1.5,
				minWidth: 0,
				minHeight: 0,
				// desktop: rows and keys stop growing; tablets never get this tall
				maxHeight: landscape ? 620 : undefined,
			}}
		>
			<Paper
				sx={{
					...CardPaperSx,
					p: 0,
					flex: 1,
					minWidth: 0,
					minHeight: 0,
					overflowY: "auto",
				}}
			>
				<Box sx={{ p: 1.5, pb: 1, display: "flex", gap: 1.5 }}>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography
							component="div"
							sx={{
								fontWeight: 700,
								fontSize: 20,
								display: "flex",
								flexWrap: "wrap",
								alignItems: "center",
								columnGap: 1.5,
							}}
						>
							{moment(cut.StartTime).format("HH:mm")} · {cutLabel(cut)}
							<QualityStatusChip {...status} />
						</Typography>
						<Box
							sx={{
								mt: 0.75,
								display: "grid",
								gridTemplateColumns: "auto minmax(0, 1fr)",
								columnGap: 1.5,
								rowGap: 0.5,
								alignItems: "baseline",
								"& > :nth-of-type(even)": {
									fontSize: 15,
									overflowWrap: "anywhere",
								},
							}}
						>
							<Typography sx={capsSx}>Part #</Typography>
							<Typography>
								{cut.PartNumber ?? (cut.IsCutback ? "Cutback" : "—")}
							</Typography>
							<Typography sx={capsSx}>Work order</Typography>
							<Typography>{cut.WorkOrderNumber ?? "—"}</Typography>
						</Box>
					</Box>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 0.5,
							"& button": { minHeight: 40, minWidth: 112 },
						}}
					>
						<Button
							variant="contained"
							sx={{ bgcolor: "#7e57c2", "&:hover": { bgcolor: "#673ab7" } }}
							disabled={locked || draft != null}
							onClick={() => requireEmployee(setReclassifying)}
						>
							{cut.IsReclassified ||
							cut.ReclassifiedToProductId != null ||
							cut.IsCutback
								? "Restore"
								: "Reclassify"}
						</Button>
						{!cut.IsCutback && (
							<Button
								variant="contained"
								color="warning"
								disabled={locked || draft != null}
								onClick={() =>
									setConfirm({
										title: "Send to Cutback?",
										message:
											"The bundle will be unlinked from its work order and set aside as material to re-cut. Continue?",
										confirmLabel: "Send to Cutback",
										onConfirm: () =>
											requireEmployee(
												(e) =>
													void run(
														() => reclassifyBundle(cut.BundleId, null, true, e),
														`${cutLabel(cut)} sent to Cutback`,
														"Unable to send the cut to Cutback.",
													),
											),
									})
								}
							>
								Cutback
							</Button>
						)}
					</Box>
				</Box>
				{/* fixed widths, "—" and nbsp: same box with or without bounds, no reflow */}
				<Box
					sx={{
						mx: 1.5,
						px: 1.5,
						py: 0.75,
						bgcolor: "grey.50",
						border: "1px solid",
						borderColor: "divider",
						borderRadius: 1,
						display: "flex",
						alignItems: "center",
						columnGap: 2,
					}}
				>
					<Box sx={{ width: landscape ? 112 : 136, flexShrink: 0 }}>
						<Typography sx={capsSx}>Tolerance</Typography>
						{shownOut ? (
							<Typography
								sx={{
									display: "flex",
									gap: 0.5,
									fontSize: 14,
									fontWeight: 700,
									color: "warning.dark",
								}}
							>
								<WarningAmberRounded sx={{ fontSize: 18 }} />
								Out of tolerance
							</Typography>
						) : (
							!hasBounds && (
								<Typography sx={{ fontSize: 14, fontWeight: 700 }}>
									{noTolerance}
								</Typography>
							)
						)}
					</Box>
					<Divider orientation="vertical" flexItem />
					{(
						[
							["Min", editingCut.MinLength],
							["Max", editingCut.MaxLength],
						] as const
					).map(([label, value]) => (
						<Box key={label} sx={{ flex: 1, minWidth: 0 }}>
							<Typography sx={capsSx}>{label}</Typography>
							<Box
								sx={{
									display: "flex",
									flexDirection: landscape ? "column" : "row",
									alignItems: landscape ? "flex-start" : "baseline",
									columnGap: 1,
								}}
							>
								<Typography
									sx={{
										fontSize: 22,
										fontWeight: 700,
										lineHeight: 1.2,
										fontVariantNumeric: "tabular-nums",
									}}
								>
									{value == null ? "—" : `${value}″`}
								</Typography>
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ whiteSpace: "nowrap" }}
								>
									{value == null ? "\u00a0" : toFraction32(value)}
								</Typography>
							</Box>
						</Box>
					))}
				</Box>
				<MeasurementFace
					cut={editingCut}
					values={values}
					position={position}
					typing={typing}
					disabled={locked}
					onSelect={pick}
				/>
				{!landscape && (
					<>
						<Box sx={{ display: "flex", flex: 1.2, px: 1.5, mb: 1.5 }}>
							{keypad}
						</Box>
						{footer}
					</>
				)}
			</Paper>
			{landscape && (
				<Paper
					sx={{
						...CardPaperSx,
						p: 0,
						width: { xs: 360, lg: 440, xl: 520 },
						flexShrink: 0,
						minHeight: 0,
					}}
				>
					<Box sx={{ display: "flex", flex: 1, minHeight: 0, p: 1.5 }}>
						{keypad}
					</Box>
					{footer}
				</Paper>
			)}
			{confirm && (
				<TabletConfirm
					{...confirm}
					onClose={(confirmed) => {
						setConfirm(null);
						if (confirmed) confirm.onConfirm();
					}}
				/>
			)}
			{reclassifying != null && (
				<ReclassifyBundleModal
					open
					touch
					assetId={cut.AssetId}
					bundleId={cut.BundleId}
					employeeNumber={reclassifying}
					onClose={() => setReclassifying(null)}
					onReclassified={() => {
						refresh().catch((failure) =>
							setError(
								failure instanceof Error
									? failure.message
									: "Unable to refresh the cut.",
							),
						);
					}}
				/>
			)}
			<Snackbar
				open={error !== ""}
				onClose={() => setError("")}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					severity="error"
					onClose={() => setError("")}
					sx={{ maxWidth: 600 }}
				>
					{error}
				</Alert>
			</Snackbar>
		</Box>
	);
};
